import { asMemberId, type MemberId } from "./member-identity";
import type { TreatmentComponentValue } from "./value.model";

export type StandardObservationKey = "tolerance";

/** Read-time charting contract shared by standards and authored observations. */
export interface TreatmentObservationDeclaration {
  id: MemberId;
  code?: string;
  label: string;
  repeatable: boolean;
  noteLabel?: string;
  components: readonly {
    id: MemberId;
    /** Platform declarations may have no external code; authored components require one. */
    code?: string;
    label: string;
    value: TreatmentComponentValue;
  }[];
}

// Permanent reserved identities: never change or reuse these ids; recorded
// observations cite them. The authored sequences use obs/cmp/prm/dev/opt,
// so none can mint an identity in the stdobs/stdcmp/stdopt namespaces.
export const TOLERANCE_OBSERVATION: TreatmentObservationDeclaration = Object.freeze({
  id: asMemberId("stdobs-1"),
  label: "Tolerance",
  repeatable: false,
  components: Object.freeze([
    Object.freeze({
      id: asMemberId("stdcmp-1"),
      label: "Tolerance",
      value: Object.freeze({
        kind: "Choice" as const,
        cardinality: "Single" as const,
        allowFreeText: false,
        // The scale is anchored to what the clinician did rather than to an
        // adjective: anchors get better inter-rater agreement than adjectives.
        // Adding an option later is free; changing or removing one orphans
        // every reading that cites it.
        //
        // CLOSED ON PURPOSE. A typed answer sits on no point of the scale and
        // cannot be compared across procedures, which is the whole reason this
        // observation is shared. Anything the five cannot express belongs in
        // the procedure note.
        options: Object.freeze([
          Object.freeze({ id: asMemberId("stdopt-1"), label: "Tolerated well" }),
          Object.freeze({ id: asMemberId("stdopt-2"), label: "Mild discomfort" }),
          Object.freeze({ id: asMemberId("stdopt-3"), label: "Moderate discomfort" }),
          Object.freeze({ id: asMemberId("stdopt-4"), label: "Poorly tolerated" }),
          Object.freeze({ id: asMemberId("stdopt-5"), label: "Not tolerated" }),
        ]),
      }),
    }),
  ]),
});

export const STANDARD_OBSERVATIONS: Readonly<Record<StandardObservationKey, TreatmentObservationDeclaration>> =
  Object.freeze({
    tolerance: TOLERANCE_OBSERVATION,
  });

export const DEFAULT_STANDARD_OBSERVATIONS: readonly StandardObservationKey[] = Object.freeze(["tolerance"]);
